import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Target,
  Cpu,
  Palette,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

/**
 * Mini visual "preview" tiles used inside each platform card.
 * Built with pure CSS/SVG to stay light.
 */

function DashboardMock() {
  return (
    <div className="relative h-40 rounded-xl overflow-hidden bg-gradient-to-br from-[#050B26] to-[#0a1142] border border-white/10 p-3">
      <div className="flex items-center justify-between text-[8px] text-white/50 mb-2">
        <span>Ad Manager</span>
        <span className="inline-flex items-center gap-1 text-emerald-400">
          <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {["Imp", "CTR", "Rev"].map((l, i) => (
          <div
            key={l}
            className="rounded-md bg-white/5 border border-white/10 p-1.5"
          >
            <div className="text-[7px] text-white/45">{l}</div>
            <div className="text-[10px] font-mono font-bold text-[#00F0FF]">
              {["2.4M", "3.2%", "$12K"][i]}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-0.5 h-14">
        {[40, 55, 35, 70, 60, 85, 45, 75, 90, 65, 55, 80, 70, 92].map((v, i) => (
          <div
            key={i}
            className="flex-1 rounded-t bg-gradient-to-t from-[#0047FF]/40 to-[#00F0FF]"
            style={{ height: `${v}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function TargetingMock() {
  return (
    <div className="relative h-40 rounded-xl overflow-hidden bg-gradient-to-br from-[#0a0a2e] to-[#1a0a3e] border border-white/10 grid place-items-center">
      <svg
        viewBox="0 0 200 160"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Concentric targeting rings */}
        {[64, 48, 32, 18].map((r, i) => (
          <circle
            key={r}
            cx="100"
            cy="80"
            r={r}
            fill="none"
            stroke={i % 2 === 0 ? "#00F0FF" : "#0047FF"}
            strokeOpacity={0.35 - i * 0.04}
            strokeWidth="1"
          />
        ))}
        {/* User dots */}
        {[
          [60, 50],
          [140, 45],
          [70, 110],
          [135, 115],
          [100, 25],
          [40, 80],
          [160, 80],
          [100, 135],
          [85, 70],
          [120, 90],
        ].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill="#00F0FF" opacity="0.9" />
            <circle cx={x} cy={y} r="6" fill="#00F0FF" opacity="0.2">
              <animate
                attributeName="r"
                values="3;9;3"
                dur="2.5s"
                begin={`${i * 0.2}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.4;0;0.4"
                dur="2.5s"
                begin={`${i * 0.2}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ))}
        {/* Center crosshair */}
        <circle cx="100" cy="80" r="4" fill="#fff" />
        <circle cx="100" cy="80" r="8" fill="none" stroke="#00F0FF" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

function MLMock() {
  // Animated neural network
  const layers = [
    [25, 55, 85, 115],
    [40, 80, 120],
    [55, 105],
    [80],
  ];
  const xPositions = [25, 70, 115, 165];
  return (
    <div className="relative h-40 rounded-xl overflow-hidden bg-gradient-to-br from-[#0a0518] to-[#1f0a3a] border border-white/10">
      <svg viewBox="0 0 200 160" className="w-full h-full">
        {/* Connections */}
        {layers.map((nodes, li) =>
          li < layers.length - 1
            ? nodes.flatMap((y1, i) =>
                layers[li + 1].map((y2, j) => (
                  <line
                    key={`${li}-${i}-${j}`}
                    x1={xPositions[li]}
                    y1={y1 + 15}
                    x2={xPositions[li + 1]}
                    y2={y2 + 15}
                    stroke="#00F0FF"
                    strokeOpacity="0.25"
                    strokeWidth="0.5"
                  >
                    <animate
                      attributeName="stroke-opacity"
                      values="0.1;0.6;0.1"
                      dur={`${2 + ((i + j) % 3) * 0.5}s`}
                      repeatCount="indefinite"
                    />
                  </line>
                )),
              )
            : null,
        )}
        {/* Nodes */}
        {layers.map((nodes, li) =>
          nodes.map((y, i) => (
            <g key={`n-${li}-${i}`}>
              <circle
                cx={xPositions[li]}
                cy={y + 15}
                r="4"
                fill={li === layers.length - 1 ? "#00F0FF" : "#0047FF"}
              />
              <circle
                cx={xPositions[li]}
                cy={y + 15}
                r="7"
                fill="none"
                stroke="#00F0FF"
                strokeOpacity="0.3"
              >
                <animate
                  attributeName="r"
                  values="4;10;4"
                  dur={`${1.5 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  values="0.5;0;0.5"
                  dur={`${1.5 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          )),
        )}
      </svg>
    </div>
  );
}

function CreativeMock() {
  return (
    <div className="relative h-40 rounded-xl overflow-hidden bg-gradient-to-br from-[#0a1142] to-[#1a0a3e] border border-white/10 p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between text-[7px] text-white/50">
        <span>Creative Studio</span>
        <span className="text-[#00F0FF]">300×250</span>
      </div>
      <div className="flex-1 rounded-md bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/10 border border-white/10 grid place-items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,240,255,0.3),transparent_60%)]" />
        <div className="text-center relative z-10">
          <div className="font-display font-bold text-sm">YOUR BRAND</div>
          <div className="text-[8px] text-white/70 mt-0.5">Premium quality</div>
          <div className="mt-1.5 inline-block px-2 py-0.5 rounded-full bg-gradient-to-r from-[#0047FF] to-[#00F0FF] text-[7px] font-medium">
            Shop Now
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {["728×90", "300×600", "320×50", "Video"].map((s) => (
          <div
            key={s}
            className="text-[6px] text-center py-0.5 rounded bg-white/5 text-white/60"
          >
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}

function PublisherMock() {
  return (
    <div className="relative h-40 rounded-xl overflow-hidden bg-gradient-to-br from-[#050B26] to-[#0a1142] border border-white/10 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[8px] text-white/50">Earnings</span>
        <span className="text-[9px] font-mono font-bold text-emerald-400">
          +24%
        </span>
      </div>
      <div className="font-display text-2xl font-bold aurora-text leading-none">
        $48,290
      </div>
      <div className="text-[7px] text-white/45 mt-0.5">This month</div>

      {/* Mini line chart */}
      <svg viewBox="0 0 180 60" className="w-full h-16 mt-2">
        <defs>
          <linearGradient id="pubLine" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,45 L20,38 L40,42 L60,30 L80,33 L100,22 L120,25 L140,15 L160,18 L180,8 L180,60 L0,60 Z"
          fill="url(#pubLine)"
        />
        <path
          d="M0,45 L20,38 L40,42 L60,30 L80,33 L100,22 L120,25 L140,15 L160,18 L180,8"
          fill="none"
          stroke="#00F0FF"
          strokeWidth="1.5"
        />
      </svg>
      <div className="text-[7px] text-white/45 text-right">NET 60 · 98% Fill</div>
    </div>
  );
}

const cards = [
  {
    icon: LayoutDashboard,
    title: "Ad Manager",
    desc: "Unified control plane for every campaign, bid, and creative variant — built for ops teams that ship daily.",
    mock: <DashboardMock />,
    accent: "from-[#0047FF] to-[#00F0FF]",
    side: "From Advertisers",
  },
  {
    icon: Target,
    title: "Smart Targeting",
    desc: "Demographic, behavioural, contextual, and lookalike audiences refined by first-party signals.",
    mock: <TargetingMock />,
    accent: "from-[#00F0FF] to-[#8A2BE2]",
    side: "From Advertisers",
  },
  {
    icon: Cpu,
    title: "Machine Learning",
    desc: "Real-time bid shading, fraud detection, and yield optimization powered by purpose-built models.",
    mock: <MLMock />,
    accent: "from-[#8A2BE2] to-[#0047FF]",
    side: "Engine",
  },
  {
    icon: Palette,
    title: "Creative Studio",
    desc: "Generate, A/B test, and dynamically optimize creatives across every IAB format — from banner to video.",
    mock: <CreativeMock />,
    accent: "from-[#0047FF] to-[#8A2BE2]",
    side: "To Publishers",
  },
  {
    icon: TrendingUp,
    title: "Publisher Suite",
    desc: "Header bidding, transparent reporting, NET-60 payouts — the entire monetization stack in one place.",
    mock: <PublisherMock />,
    accent: "from-[#00F0FF] to-[#0047FF]",
    side: "To Publishers",
  },
];

export default function GlobalPlatform() {
  return (
    <section
      id="platform"
      data-testid="section-platform"
      className="relative py-20"
    >
      <div className="aurora-orb bg-[#00F0FF] w-[600px] h-[600px] -top-40 right-0 opacity-15" />
      <div className="aurora-orb bg-[#8A2BE2] w-[500px] h-[500px] bottom-0 -left-40 opacity-15" />

      <div className="container-x relative">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
            One Platform · End-to-End
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-6">
            The <span className="aurora-text">Global Ad Platform</span>
          </h2>
          <p className="text-white/65 text-lg leading-relaxed">
            By integrating every layer of the ad stack — buyer, engine, seller —
            into a single end-to-end platform, MonetiseUP delivers superior
            results for advertisers and exceptional monetization for publishers.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`platform-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative rounded-3xl border border-white/10 bg-white/[0.025] backdrop-blur-md p-6 hover:border-[#00F0FF]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              {/* Decorative accent glow */}
              <div
                className={`absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br ${c.accent} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`}
              />

              {/* Side label */}
              <div className="text-[9px] uppercase tracking-[0.25em] text-white/35 mb-4">
                {c.side}
              </div>

              {/* Mock visual */}
              <div className="mb-6">{c.mock}</div>

              {/* Icon + content */}
              <div className="flex items-start gap-3 mb-3">
                <div
                  className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${c.accent} grid place-items-center`}
                >
                  <c.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold leading-tight pt-1.5">
                  {c.title}
                </h3>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}

          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative rounded-3xl p-8 overflow-hidden flex flex-col justify-between min-h-[420px] border border-[#00F0FF]/30 bg-gradient-to-br from-[#0047FF]/20 via-[#00F0FF]/10 to-transparent"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(0,240,255,0.2),transparent_60%)]" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
                One Login · Every Tool
              </div>
              <h3 className="font-display text-3xl font-bold leading-tight mb-4">
                Discover the
                <br />
                <span className="aurora-text">Global Ad Platform</span>
              </h3>
              <p className="text-sm text-white/65">
                See how publishers and advertisers operate side-by-side on the
                same rail.
              </p>
            </div>
            <Link
              to="/demo"
              data-testid="platform-cta-discover"
              className="btn-primary self-start mt-8"
            >
              Explore the Platform <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
